import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getPortfolioSummaryReport,
  downloadPortfolioSummaryPDF,
} from '../../services/portfolioSummaryService';
import {
  formatCurrency,
  formatPercentage,
  getGainLossColor,
  isValidReportData,
  calculateGroupTotal,
  downloadPDF,
  getEmptyStateMessage,
  getGroupAllocationPercentage,
} from '../../utils/portfolioSummaryReportHelpers';
import '../../assets/css/Transaction-Section-Style/portfolioSummaryReport.css';

/**
 * PortfolioSummaryReport Component
 * 
 * Displays a consolidated portfolio summary report with:
 * - Asset classes grouped by category (Direct Equity, Mutual Funds, etc.)
 * - Invested amount, current value, gain/loss, and return percentage
 * - Grand totals row with overall portfolio metrics
 * - Responsive table with sticky headers
 * - PDF download and print functionality
 * - Professional enterprise financial reporting UI
 * 
 * API Response Shape:
 * {
 *   "asset_groups": [
 *     {
 *       "group_name": "Direct Equity",
 *       "items": [
 *         {
 *           "asset_name": "Investment Account",
 *           "invested_amount": 2000000.00,
 *           "current_value": 2150000.00,
 *           "gain_loss": 150000.00,
 *           "gain_loss_percent": 7.50
 *         },
 *         {
 *           "asset_name": "Trading Account",
 *           "invested_amount": 1500000.00,
 *           "current_value": 1620000.00,
 *           "gain_loss": 120000.00,
 *           "gain_loss_percent": 8.00
 *         }
 *       ]
 *     },
 *     {
 *       "group_name": "Mutual Funds",
 *       "items": [...]
 *     }
 *   ],
 *   "grand_total": {
 *     "invested_amount": 18100000.00,
 *     "current_value": 20387000.00,
 *     "gain_loss": 2287000.00,
 *     "gain_loss_percent": 12.64
 *   }
 * }
 */
const PortfolioSummaryReport = () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ─── Data Fetch ────────────────────────────────────────────────────────────

  /**
   * Fetch portfolio summary report from API
   */
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPortfolioSummaryReport();
      
      if (!isValidReportData(data)) {
        throw new Error('Invalid report data structure');
      }

      setReportData(data);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to fetch portfolio summary report';
      setError(msg);
      toast.error(msg);
      console.error('Error fetching portfolio summary report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize report on component mount
   */
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  /**
   * Download report as PDF
   */
  const handleDownloadPDF = useCallback(async () => {
    try {
      setLoading(true);
      const blob = await downloadPortfolioSummaryPDF();
      downloadPDF(blob, 'Portfolio_Summary_Report');
      toast.success('PDF downloaded successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to download PDF';
      toast.error(msg);
      console.error('Error downloading PDF:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Print report
   */
  const handlePrint = useCallback(async () => {
    try {
      setLoading(true);
      const blob = await downloadPortfolioSummaryPDF();
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      } else {
        toast.info('Print window blocked. Using browser print instead.');
        window.print();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to print report';
      toast.error(msg);
      console.error('Error printing report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Go back to previous page
   */
  const handleBack = () => window.history.back();

  // ─── Render Helpers ──────────────────────────────────────────────────────────

  /**
   * Render error banner
   */
  const renderErrorBanner = () => {
    if (!error) return null;
    return (
      <div className="psr-error">
        <span>
          <strong>Error:</strong> {error}
        </span>
        <button onClick={() => setError(null)} title="Dismiss">
          ✕
        </button>
      </div>
    );
  };

  /**
   * Render loading spinner
   */
  const renderLoading = () => (
    <div className="psr-state-box">
      <div className="psr-spinner" />
    </div>
  );

  /**
   * Render empty state
   */
  const renderEmpty = () => {
    const { title, message } = getEmptyStateMessage(false, true);
    return (
      <div className="psr-state-box">
        <h3>{title}</h3>
        <p>{message}</p>
      </div>
    );
  };

  /**
   * Render main report table
   */
  const renderTable = () => {
    if (loading) return renderLoading();
    if (!reportData || !reportData.asset_groups || reportData.asset_groups.length === 0) {
      return renderEmpty();
    }

    const assetGroups = reportData.asset_groups;
    const grandTotal = reportData.grand_total || {};

    return (
      <div className="portfolio-summary-table-container">
        <div className="portfolio-summary-table-wrapper">
          <table className="portfolio-summary-table">
            {/* ── Header ── */}
            <thead>
              <tr>
                <th className="asset-class-header">Asset Class</th>
                <th>Invested (₹)</th>
                <th>Current (₹)</th>
                <th>Gain / Loss (₹)</th>
                <th>G/L %</th>
              </tr>
            </thead>

            {/* ── Body ── */}
            <tbody>
              {assetGroups.map((group, groupIndex) => {
                const groupItems = group.items || [];
                const groupTotal = calculateGroupTotal(groupItems);
                const groupAllocation = getGroupAllocationPercentage(
                  groupTotal,
                  grandTotal.invested_amount || 0
                );

                return (
                  <React.Fragment key={`group-${groupIndex}`}>
                    {/* ── Group Header ── */}
                    <tr className="group-header">
                      <td className="asset-class-cell">
                        <span className="psr-group-label">{group.group_name}</span>
                        {group.group_name}
                      </td>
                      <td className="number-cell">
                        {formatCurrency(groupTotal.invested_amount)}
                      </td>
                      <td className="number-cell">
                        {formatCurrency(groupTotal.current_value)}
                      </td>
                      <td className={`number-cell ${getGainLossColor(groupTotal.gain_loss)}`}>
                        {formatCurrency(groupTotal.gain_loss)}
                      </td>
                      <td className={`number-cell ${getGainLossColor(groupTotal.gain_loss_percent)}`}>
                        {formatPercentage(groupTotal.gain_loss_percent)}
                      </td>
                    </tr>

                    {/* ── Asset Items ── */}
                    {groupItems.map((item, itemIndex) => (
                      <tr key={`item-${groupIndex}-${itemIndex}`} className="asset-row">
                        <td className="asset-class-cell">
                          &nbsp;&nbsp;&nbsp;&nbsp;{item.asset_name}
                        </td>
                        <td className="number-cell">
                          {formatCurrency(item.invested_amount)}
                        </td>
                        <td className="number-cell">
                          {formatCurrency(item.current_value)}
                        </td>
                        <td className={`number-cell ${getGainLossColor(item.gain_loss)}`}>
                          {formatCurrency(item.gain_loss)}
                        </td>
                        <td className={`number-cell ${getGainLossColor(item.gain_loss_percent)}`}>
                          {formatPercentage(item.gain_loss_percent)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}

              {/* ── Grand Total Row ── */}
              <tr className="grand-total-row">
                <td className="asset-class-cell">GRAND TOTAL</td>
                <td className="number-cell">
                  {formatCurrency(grandTotal.invested_amount)}
                </td>
                <td className="number-cell">
                  {formatCurrency(grandTotal.current_value)}
                </td>
                <td className="number-cell">
                  {formatCurrency(grandTotal.gain_loss)}
                </td>
                <td className="number-cell">
                  {formatPercentage(grandTotal.gain_loss_percent)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /**
   * Render info note
   */
  const renderInfoNote = () => {
    if (!reportData) return null;
    return (
      <div className="psr-info-note">
        <span className="psr-info-icon">ℹ</span>
        <span>
          Portfolio values are updated based on your latest market data. 
          Gains and losses are calculated from your original invested amounts.
        </span>
      </div>
    );
  };

  /**
   * Render summary metrics cards
   */
  const renderSummaryMetrics = () => {
    if (!reportData || !reportData.grand_total) return null;

    const gt = reportData.grand_total;
    const gainLossClass = getGainLossColor(gt.gain_loss);
    const gainPctClass = getGainLossColor(gt.gain_loss_percent);

    return (
      <div style={{ display: 'none' }}>
        {/* Hidden summary for structure but shown via CSS in print */}
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────────

  return (
    <div className="portfolio-summary-wrapper">
      {/* ── Top Controls ── */}
      <div className="portfolio-summary-top-controls">
        <div className="portfolio-summary-left-controls">
          <a
            href="#"
            className="portfolio-summary-back-button"
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
            title="Go back to previous page"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </a>

          <button
            className="portfolio-summary-print-button"
            onClick={handlePrint}
            disabled={loading || !reportData}
            title="Print the portfolio summary report"
          >
            Print
          </button>

          <button
            className="portfolio-summary-download-button"
            onClick={handleDownloadPDF}
            disabled={loading || !reportData}
            title="Download portfolio summary report as PDF"
          >
            Download
          </button>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {renderErrorBanner()}

      {/* ── Main Container ── */}
      <div className="portfolio-summary-main-container">
        <h1 className="portfolio-summary-main-title">Portfolio Summary Report</h1>
        <p className="portfolio-summary-main-subtitle">
          Consolidated asset allocation and performance across all asset classes
        </p>
        <hr className="portfolio-summary-divider" />

        {/* ── Summary Metrics ── */}
        {renderSummaryMetrics()}

        {/* ── Report Table ── */}
        {renderTable()}

        {/* ── Info Note ── */}
        {renderInfoNote()}
      </div>
    </div>
  );
};

export default PortfolioSummaryReport;
